import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Copy, Check } from "lucide-react";

/**
 * Renders the multi-step MFA UI after a successful admin password login.
 *
 * mfaData shape (from AuthContext.login):
 *   { mfa: "required", challenge }
 *   { mfa: "enroll",  challenge, otpauth_url, secret }
 *
 * onDone(user) is called with the final logged-in user record so the
 * parent (Login page) can navigate.
 */
export function MfaPanel({ mfaData, onDone }) {
  const { verifyTotp, enrollTotp, recoverMfa } = useAuth();
  const { t } = useLang();
  const isEnroll = mfaData.mfa === "enroll";

  // For "required" flow we let the admin optionally switch to a recovery code.
  const [mode, setMode] = useState(isEnroll ? "enroll" : "totp"); // "totp" | "recovery" | "enroll" | "recovery-codes"
  const [code, setCode] = useState("");
  const [recoveryInput, setRecoveryInput] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const doVerify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = isEnroll
        ? await enrollTotp(mfaData.challenge, code.trim())
        : await verifyTotp(mfaData.challenge, code.trim());
      if (res.recovery_codes) {
        setRecoveryCodes(res.recovery_codes);
        setMode("recovery-codes");
      } else {
        toast.success(t("mfa_verified"));
        onDone(res.user);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doRecovery = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await recoverMfa(mfaData.challenge, recoveryInput.trim());
      toast.success(t("mfa_recovered"));
      onDone(res.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(mfaData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("copy failed:", err);
    }
  };

  const copyRecovery = async () => {
    if (!recoveryCodes) return;
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success(t("recovery_copied"));
    } catch (err) {
      console.error("copy failed:", err);
    }
  };

  // -----  Recovery-codes shown ONCE screen  -----
  if (mode === "recovery-codes" && recoveryCodes) {
    return (
      <div className="space-y-6" data-testid="mfa-recovery-codes-panel">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#F5A623]/10 border border-[#F5A623]/40 flex items-center justify-center">
            <KeyRound size={18} className="text-[#F5A623]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5A623]">{t("mfa_recovery_title_kicker")}</div>
            <div className="font-display text-2xl">{t("mfa_recovery_title")}</div>
          </div>
        </div>
        <p className="text-sm text-[#9CA3AF]">{t("mfa_recovery_instructions")}</p>
        <ul
          data-testid="mfa-recovery-codes-list"
          className="grid grid-cols-2 gap-2 font-mono text-sm bg-[#0A0A0A] border border-[#262626] rounded-sm p-4"
        >
          {recoveryCodes.map((c) => (
            <li key={c} className="tracking-widest">{c}</li>
          ))}
        </ul>
        <div className="flex gap-3">
          <button
            data-testid="mfa-copy-recovery-btn"
            type="button"
            onClick={copyRecovery}
            className="flex-1 border border-[#262626] hover:border-[#F5A623] text-[#F3F4F6] rounded-sm px-4 py-2.5 flex items-center justify-center gap-2 text-sm"
          >
            <Copy size={14} /> {t("copy_all")}
          </button>
          <button
            data-testid="mfa-recovery-done-btn"
            type="button"
            onClick={() => onDone()}
            className="flex-1 bg-[#F5A623] text-black font-semibold rounded-sm px-4 py-2.5"
          >
            {t("continue")}
          </button>
        </div>
      </div>
    );
  }

  // -----  Enrollment screen (QR + secret)  -----
  return (
    <div className="space-y-5" data-testid={isEnroll ? "mfa-enroll-panel" : "mfa-verify-panel"}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-sm bg-[#F5A623]/10 border border-[#F5A623]/40 flex items-center justify-center">
          <ShieldCheck size={18} className="text-[#F5A623]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5A623]">
            {t("admin_mfa_kicker")}
          </div>
          <div className="font-display text-2xl">
            {isEnroll ? t("mfa_enroll_title") : t("mfa_verify_title")}
          </div>
        </div>
      </div>

      {isEnroll && (
        <>
          <p className="text-sm text-[#9CA3AF]">{t("mfa_enroll_instructions")}</p>
          <div className="flex items-center gap-5 bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
            <div
              data-testid="mfa-qr-code"
              className="bg-white p-2 rounded-sm shrink-0"
            >
              <QRCodeSVG value={mfaData.otpauth_url} size={140} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-1">
                {t("mfa_manual_secret")}
              </div>
              <div
                data-testid="mfa-secret-value"
                className="font-mono text-xs break-all text-[#F3F4F6] mb-2"
              >
                {mfaData.secret}
              </div>
              <button
                type="button"
                onClick={copySecret}
                data-testid="mfa-copy-secret-btn"
                className="text-[10px] uppercase tracking-[0.2em] text-[#F5A623] hover:underline flex items-center gap-1"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? t("copied") : t("copy_secret")}
              </button>
            </div>
          </div>
        </>
      )}

      {mode !== "recovery" ? (
        <form onSubmit={doVerify} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-1.5 block">
              {t("mfa_code_label")}
            </label>
            <input
              data-testid="mfa-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              placeholder="123456"
              className="w-full bg-[#0A0A0A] border border-[#262626] text-[#F3F4F6] rounded-sm px-4 py-3 tracking-[0.6em] font-mono text-lg focus:outline-none focus:border-[#F5A623]"
            />
          </div>
          {error && (
            <div className="text-sm text-[#DC2626] border border-[#DC2626]/30 bg-[#DC2626]/5 px-3 py-2 rounded-sm" data-testid="mfa-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid="mfa-submit-btn"
            disabled={busy || code.length !== 6}
            className="w-full bg-[#F5A623] text-black font-semibold rounded-sm px-6 py-3 disabled:opacity-50"
          >
            {busy ? t("loading") : isEnroll ? t("mfa_enroll_submit") : t("mfa_verify_submit")}
          </button>
          {!isEnroll && (
            <button
              type="button"
              onClick={() => { setMode("recovery"); setError(""); }}
              data-testid="mfa-use-recovery-btn"
              className="w-full text-xs text-[#9CA3AF] hover:text-[#F5A623] uppercase tracking-[0.2em]"
            >
              {t("mfa_use_recovery")}
            </button>
          )}
        </form>
      ) : (
        <form onSubmit={doRecovery} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-1.5 block">
              {t("mfa_recovery_code_label")}
            </label>
            <input
              data-testid="mfa-recovery-input"
              value={recoveryInput}
              onChange={(e) => setRecoveryInput(e.target.value.toUpperCase())}
              autoFocus
              placeholder="XXXX-XXXX"
              className="w-full bg-[#0A0A0A] border border-[#262626] text-[#F3F4F6] rounded-sm px-4 py-3 tracking-[0.3em] font-mono focus:outline-none focus:border-[#F5A623]"
            />
          </div>
          {error && (
            <div className="text-sm text-[#DC2626] border border-[#DC2626]/30 bg-[#DC2626]/5 px-3 py-2 rounded-sm" data-testid="mfa-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid="mfa-recovery-submit-btn"
            disabled={busy || recoveryInput.length < 6}
            className="w-full bg-[#F5A623] text-black font-semibold rounded-sm px-6 py-3 disabled:opacity-50"
          >
            {busy ? t("loading") : t("mfa_recovery_submit")}
          </button>
          <button
            type="button"
            onClick={() => { setMode("totp"); setError(""); }}
            data-testid="mfa-back-to-totp-btn"
            className="w-full text-xs text-[#9CA3AF] hover:text-[#F5A623] uppercase tracking-[0.2em]"
          >
            {t("mfa_back_to_totp")}
          </button>
        </form>
      )}
    </div>
  );
}
